/**
 * RegisterExecutor — account creation page for executor invite links.
 * Accessed at /register?token=<inviteToken>
 */

import { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { checkBreachedPassword } from "@/lib/hibpCheck";
import { validatePasswordComplexity, PASSWORD_COMPLEXITY_MESSAGE } from "@/lib/passwordStrength";
import { PRODUCT_NAME } from "@shared/brand";

export default function RegisterExecutor() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const token = params.get("token") ?? "";
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [breachCount, setBreachCount] = useState<number | null>(null);
  const [passwordError, setPasswordError] = useState("");

  // Fetch invite info
  const { data: invite, isLoading, error } = useQuery<{
    executorAccessId: string;
    name: string;
    email: string;
    clientName: string;
  }>({
    queryKey: ["/api/auth/executor-invite", token],
    queryFn: async () => {
      const res = await fetch(`/api/auth/executor-invite/${token}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Invalid invite link");
      }
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  // Pre-fill name from invite
  const displayName = fullName || invite?.name || "";

  const registerFlight = useSingleFlight();

  const registerMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/auth/register-executor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, fullName: displayName }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Registration failed");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast({ title: "Account created", description: `Welcome to ${PRODUCT_NAME}.` });
      queryClient.clear();
      setLocation("/dashboard");
    },
    onError: (err: any) => {
      toast({ title: "Registration failed", description: err.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    if (!validatePasswordComplexity(password)) {
      setPasswordError(PASSWORD_COMPLEXITY_MESSAGE);
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords don't match");
      return;
    }
    // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire attempts to create an executor account.
    registerFlight.runQuiet(() => registerMutation.mutateAsync());
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-3">
            <AlertTriangle className="w-8 h-8 text-destructive mx-auto" />
            <p className="font-medium">Invalid invite link</p>
            <p className="text-sm text-muted-foreground">This link is missing a token. Please use the link from your invitation email.</p>
            <Button variant="outline" onClick={() => setLocation("/login")}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-3">
            <AlertTriangle className="w-8 h-8 text-destructive mx-auto" />
            <p className="font-medium">Invite link invalid or expired</p>
            <p className="text-sm text-muted-foreground">
              {(error as any)?.message || "This invitation link is no longer valid. Please ask your advisor to resend the invitation."}
            </p>
            <Button variant="outline" onClick={() => setLocation("/login")}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center space-y-1">
          <div className="flex justify-center mb-3">
            <div className="rounded-full bg-primary/10 p-3">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-bold">Create Your Account</h1>
          <p className="text-sm text-muted-foreground">
            You've been named as an executor for <strong>{invite.clientName}</strong>'s estate plan.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Set up your account</CardTitle>
            <CardDescription>Your email is pre-filled from the invitation.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={invite.email}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={invite.name}
                  required
                  data-testid="input-full-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setBreachCount(null); }}
                  onBlur={async (e) => {
                    const pwd = e.target.value;
                    if (pwd.length >= 10) {
                      const count = await checkBreachedPassword(pwd);
                      setBreachCount(count);
                    }
                  }}
                  placeholder="At least 10 characters"
                  required
                  data-testid="input-password"
                />
                <PasswordStrengthMeter password={password} />
                {breachCount !== null && breachCount > 0 && (
                  <p className="text-xs mt-1" style={{ color: "#C53030" }}>
                    <AlertTriangle className="h-3.5 w-3.5 inline-block shrink-0 align-[-2px]" aria-hidden="true" /> This password has appeared in {breachCount.toLocaleString()} data breach{breachCount === 1 ? "" : "es"}. Choose a different password.
                  </p>
                )}
                {passwordError && <p className="text-xs mt-1 text-destructive">{passwordError}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  data-testid="input-confirm-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={registerFlight.inFlight || registerMutation.isPending}
                data-testid="button-create-account"
              >
                {registerMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account…
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <a href="/login" className="text-primary hover:underline">
            Log in
          </a>
        </p>
      </div>
    </div>
  );
}
